import { memo, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { CalendarMonthOutlined } from "@mui/icons-material";
import theme from "themeProvider";
import { formatEventName } from "../../eventIconMap";
import { makeEventDetails } from "../../makeData";
import { formatDateTime } from "../../utils";
import EventCardHeader from "./EventCardHeader";
import EventDetailsTrigger from "./EventDetailsTrigger";
import { CARD_WIDTH } from "./constants";

const EventCard = ({ event, Icon, color, isLatest }) => {
  const details = useMemo(() => makeEventDetails(event), [event]);
  const title = formatEventName(event.eventName) || "Unknown Event";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        background: isLatest ? alpha(color, 0.05) : theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderTop: `3px solid ${color}`,
        borderBottom: `3px solid ${color}`,
        borderRadius: 1,
        padding: "10px 12px",
        width: CARD_WIDTH,
        flexShrink: 0,
        boxShadow: isLatest
          ? `0 0 0 3px ${alpha(color, 0.22)}, 0 4px 14px ${alpha(color, 0.3)}`
          : "0 1px 2px rgba(16, 24, 40, 0.04)",
      }}
    >
      <EventCardHeader
        Icon={Icon}
        color={color}
        eventSrc={event.eventSrc}
        fromCache={!!event.fromCache}
        retry={event.retry}
        isLatest={isLatest}
      />
      <Typography
        title={title}
        sx={{
          fontSize: 13,
          fontWeight: 700,
          color: theme.palette.text.primary,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        <CalendarMonthOutlined
          sx={{ fontSize: 12, color: theme.palette.text.secondary }}
        />
        <Typography
          sx={{
            fontSize: 11,
            color: theme.palette.text.secondary,
            whiteSpace: "nowrap",
          }}
        >
          {formatDateTime(event.receivedAt, "DD MMM, hh:mm:ss A")}
        </Typography>
      </Box>
      {details && (
        <EventDetailsTrigger
          event={event}
          Icon={Icon}
          color={color}
          details={details}
        />
      )}
    </Box>
  );
};

export default memo(EventCard);
